import React from 'react';
import styled from 'styled-components';
import { Booking } from '../types';

interface BookingConfirmationProps {
    confirmationData: Booking;
    clearConfirmationData: () => void;
}

function BookingConfirmation({
    confirmationData,
    clearConfirmationData,
}: BookingConfirmationProps) {
    const startDate = new Date(confirmationData.startTime);
    const endDate = new Date(confirmationData.endTime);

    const formatOptions: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    };

    const timeString = `${startDate.toLocaleDateString(
        undefined,
        formatOptions
    )} - ${endDate.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
    })}`;

    return (
        <Wrapper>
            <Header>
                <Emoji>
                    <svg viewBox='0 0 24 24'>
                        <polyline points='4 12 10 18 20 6' />
                    </svg>
                </Emoji>
                <Title>Meeting Confirmed</Title>
                <Subtitle>
                    An email invitation has been sent to all participants.
                </Subtitle>
            </Header>
            <Details>
                <Detail>
                    <Label>What:</Label>
                    <Value>{confirmationData.title}</Value>
                </Detail>
                <Detail>
                    <Label>When:</Label>
                    <Value>{timeString}</Value>
                </Detail>
                <Detail>
                    <Label>With:</Label>
                    <Value>{confirmationData.user.name}</Value>
                </Detail>
                <Detail>
                    <Label>Where:</Label>
                    <Value>
                        <a
                            href={confirmationData.videoCallUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            {confirmationData.videoCallUrl}
                        </a>
                    </Value>
                </Detail>
                <Note>
                    Need to make a change? <a href='#'>Reschedule</a> or{' '}
                    <a href='#'>Cancel</a>
                </Note>
            </Details>
            <Actions>
                <PrimaryButton>Add to calendar</PrimaryButton>
                <SecondaryButton onClick={clearConfirmationData}>
                    Back
                </SecondaryButton>
            </Actions>
        </Wrapper>
    );
}

const Wrapper = styled.div`
    background-color: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    max-width: 600px;
    margin: 0 auto;
    padding: 32px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    font-family: 'Segoe UI', sans-serif;
`;

const Header = styled.div`
    text-align: center;
    margin-bottom: 32px;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 24px;
    color: #2e2e2e;
`;

const Subtitle = styled.p`
    color: #555;
    font-size: 14px;
`;

const Details = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 32px;
`;

const Detail = styled.div`
    display: flex;
    gap: 8px;
`;

const Label = styled.span`
    font-weight: 600;
    color: #444;
    width: 60px;
`;

const Value = styled.span`
    color: #2e2e2e;
    word-break: break-word;

    a {
        color: #0077cc;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const Note = styled.div`
    margin-top: 8px;
    font-size: 14px;
    color: #444;

    a {
        margin-left: 4px;
        color: #0077cc;
        cursor: pointer;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const Actions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
`;

const PrimaryButton = styled.button`
    background-color: #0077cc;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 12px 24px;
    font-size: 14px;
    cursor: pointer;

    &:hover {
        background-color: #005fa3;
    }
`;

const SecondaryButton = styled.button`
    background-color: transparent;
    color: #0077cc;
    border: 1px solid #0077cc;
    border-radius: 6px;
    padding: 10px 20px;
    font-size: 14px;
    cursor: pointer;

    &:hover {
        background-color: #f0f8ff;
    }
`;

const Emoji = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: #e0f7e9;
    margin: 0 auto 16px auto;

    svg {
        width: 32px;
        height: 32px;
        stroke: #2e7d32;
        stroke-width: 3;
        fill: none;
    }
`;
export default BookingConfirmation;
